'use client';

import React, { useEffect, useState } from 'react';

import styled, { createGlobalStyle, css, keyframes } from 'styled-components';

import { createGradientOrSolid, type Gradient } from '@/lib/gradient';

import { AddCircleOutlineIcon, CloseIcon, IosShareIcon, MoreVertIcon } from './icons';

/**
 * Portal-owned copies of shared-ui's AddToHomeScreenButton and
 * AddToHomePageModal (v0.5.383). Styled CSS is verbatim; MUI pieces are
 * replaced by the elements they render as — Box by div, Divider by an hr with
 * MUI's divider rules, IconButton by an unstyled button — and the four icons
 * by inline SVGs carrying MUI's own path data (see icons.tsx). The two android
 * instruction PNGs moved to public/add-to-home/.
 */

type AddToHomePageButtonProps = {
  onClick: () => void;
  borderColor?: string;
  backgroundColor?: Gradient;
  buttonImage?: string;
  isMockPreview?: boolean;
  translations?: Record<string, string>;
};

export const AddToHomeScreenButton: React.FC<AddToHomePageButtonProps> = ({
  onClick,
  borderColor = '#FFF',
  backgroundColor = { colorOne: '#522AD8', colorTwo: '#7946FA' },
  buttonImage = '',
  isMockPreview = false,
  translations = {
    addToHomeScreen: 'Add to Home Screen',
  },
}) => (
  <StyledAddToHomePageButton
    type={isMockPreview ? 'button' : 'submit'}
    onClick={onClick}
    borderColor={borderColor}
    bgColor={backgroundColor}
    buttonImage={buttonImage}
  >
    {!buttonImage && (
      <>
        <AddCircleOutlineIcon className="icon" style={{ color: borderColor }} />
        <div className="description">
          <div>{translations.addToHomeScreen}</div>
        </div>
      </>
    )}
  </StyledAddToHomePageButton>
);

const StyledAddToHomePageButton = styled.button`
  display: flex;
  align-items: center;
  width: ${(props: any) => (props?.buttonImage ? '122px' : 'auto')};
  height: ${(props: any) => (props?.buttonImage ? '40px' : 'auto')};
  min-width: 100px;
  text-align: left;
  border-radius: ${(props: any) => (props?.buttonImage ? 0 : '36px')};
  border: ${(props: any) => (props?.buttonImage ? 0 : `2px solid ${props?.borderColor}`)};
  background: ${(props: any) =>
    props?.buttonImage
      ? `url(${props?.buttonImage})`
      : createGradientOrSolid(props?.bgColor, true)};
  background-size: 100% 100%;
  box-shadow: ${(props: any) => (props?.buttonImage ? 0 : '0 4px 4px 0 rgba(0, 0, 0, 0.25)')};
  padding: ${(props: any) => (props?.buttonImage ? 0 : '4px 6px 4px 4px')};

  .description {
    font-family: var(--font-family), Arial;
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    text-transform: uppercase;
    color: ${(props: any) => props?.borderColor};
    padding: 0 4px;
  }
` as any;

interface AddToHomePageModalProps {
  iconImage: string;
  shortcutName: string;
  isDarkMode: boolean;
  onClick: () => void;
  translations?: Record<string, string>;
  isAndroid?: boolean;
}

export const AddToHomePageModal: React.FC<AddToHomePageModalProps> = ({
  iconImage,
  isDarkMode,
  shortcutName,
  onClick,
  translations = {
    installInstruction: `Install ${shortcutName} to get the latest deals!`,
    description:
      'This site has app functionality. Add it to your Home Screen for extensive experience and easy access!',
    moreInfoStep: '1. Click on the more icon in the top menu',
    shareStep: '1. Click on the "Share" icon in the navigation bar',
    addStep: '2. Click "Add to Home Screen"',
  },
  isAndroid = false,
}) => {
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClick();
    }, 200);
  };

  return (
    <StyledAddToHomePageModal isClosing={isClosing} isAndroid={isAndroid}>
      <ModalThemeVariables />
      <div className="modal-container">
        <button type="button" className="close-button" onClick={handleClose}>
          <CloseIcon className="close-button--icon" />
        </button>
        {!isAndroid && (
          <div className="company-info">
            <div>
              <img className="company-info--logo" alt="publisher-logo" src={iconImage} />
              <h2 className="company-info--title">{shortcutName}</h2>
              <p className="company-info--description">{translations.installInstruction}</p>
            </div>
          </div>
        )}
        <div className="guide">
          {!isAndroid && (
            <>
              <hr className="guide-divider" />
              <p className="guide-description">{translations.description}</p>
              <hr className="guide-divider" />
            </>
          )}
          <ul className="guide-list">
            <li className="guide-list--item">
              {isAndroid ? (
                <>
                  <MoreVertIcon className="guide-list--item__icon" />
                  {translations.moreInfoStep}
                </>
              ) : (
                <>
                  <IosShareIcon className="guide-list--item__icon" />
                  {translations.shareStep}
                </>
              )}
            </li>
            <li className="guide-list--item">
              {isAndroid ? (
                <img
                  src={isDarkMode ? '/add-to-home/android-dark.png' : '/add-to-home/android-light.png'}
                  alt="icon"
                  className="guide-list--item__icon"
                  style={{ color: 'black' }}
                />
              ) : (
                <i className="material-icons guide-list--item__icon add_box">add_box</i>
              )}
              {translations.addStep}
            </li>
          </ul>
        </div>
      </div>
    </StyledAddToHomePageModal>
  );
};

// Verbatim from the component's styles.css: the modal reads its colors from
// document-level [data-theme] variables it sets in the effect above.
const ModalThemeVariables = createGlobalStyle`
  :root {
    --save-to-home-page-bg-light: rgb(255 255 255 / 85%);
    --save-to-home-page-bg-dark: rgb(46 46 46 / 85%);
    --save-to-home-page-text-light: #000;
    --save-to-home-page-text-dark: #FFF;
  }

  [data-theme='light'] {
    --background-color: var(--save-to-home-page-bg-light);
    --text-color: var(--save-to-home-page-text-light);
  }

  [data-theme='dark'] {
    --background-color: var(--save-to-home-page-bg-dark);
    --text-color: var(--save-to-home-page-text-dark);
  }
`;

type AddToHomeScreenStyleProps = {
  isClosing: boolean;
  isAndroid: boolean;
};

const slideUp = keyframes`
    from {
        transform: translateY(100%);
    }
    to {
        transform: translateY(0);
    }
`;

const slideDown = keyframes`
    from {
        transform: translateY(0);
    }
    to {
        transform: translateY(100%);
    }
`;

// The .close-button and .guide-divider base rules replicate what MUI's
// IconButton and Divider contributed before their class-level overrides.
const StyledAddToHomePageModal = styled.div<AddToHomeScreenStyleProps>`
  font-family: 'Open Sans', sans-serif;
  width: 100%;
  background-color: var(--background-color);
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 100;
  border-radius: ${({ isAndroid }) => (isAndroid ? 'none' : '15px 15px 0 0')};
  backdrop-filter: blur(8px);
  color: var(--text-color);
  animation: ${({ isClosing }) => (isClosing ? slideDown : slideUp)} 0.2s
    ease-out forwards;

  .modal-container {
    margin: ${({ isAndroid }) => (isAndroid ? '16px' : '13px 16px 25px')};
    position: relative;

    .close-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: 0;
      border-radius: 50%;
      cursor: pointer;
      position: absolute;
      right: 25px; // Icon's Width
      top: ${({ isAndroid }) => (isAndroid ? 0 : '4px')};
      padding: 0;

      &--icon {
        color: var(--text-color);
      }
    }

    .company-info {
      font-weight: 600;
      font-style: normal;
      line-height: 1.5;
      margin-bottom: 24px;
      display: flex;
      justify-content: space-between;
      align-items: end;

      &--logo {
        height: 40px;
        margin-bottom: 15px;
      }

      &--title {
        font-size: 14px;
        text-transform: uppercase;
        margin: 0 0 4px 0;
      }

      &--description {
        font-size: 10px;
        margin: 0;
      }
    }

    .guide {
      font-size: 14px;
      font-style: normal;
      line-height: 1.5;
      width: 100%;

      &-divider {
        margin: 0;
        flex-shrink: 0;
        border-width: 0;
        border-style: solid;
        border-color: rgba(0, 0, 0, 0.12);
        background: #7b7b7b;
        border-bottom-width: 0.5px;
      }

      &-description {
        font-weight: 400;
        margin: 16px 0;
      }

      &-list {
        padding: 0;
        margin-top: 16px;
        margin-bottom: 0;

        &--item {
          font-weight: 600;
          display: flex;
          align-items: center;

          &:not(:last-child) {
            margin-bottom: 16px;
          }

          &__icon {
            margin-right: 5px;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            width: 24px;
            height: 24px;
          }

          .ios_share {
            font-size: 1rem;
          }

          .add_box {
            font-size: 18px;
          }
        }
      }
    }
  }
` as any;
